import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon,
  PeopleOutline,
} from '@mui/icons-material';
import { MultiAccountService } from '../services/multiAccountService';
import { GoogleUser } from '../types';
import { logger } from '../utils/logger';
import PageHeader from './common/PageHeader';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<GoogleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    user: GoogleUser | null;
    action: 'approve' | 'reject' | null;
  }>({ open: false, user: null, action: null });

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const allUsers = await MultiAccountService.getAllGoogleUsers();
      // Sort by creation date, newest first, and then by approval status
      allUsers.sort((a, b) => {
        if (a.isAllowed !== b.isAllowed) {
          return a.isAllowed ? 1 : -1; // Show pending users first
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setUsers(allUsers);
    } catch (err) {
      logger.component.error('Error loading users', err);
      // SEC-04:permission-denied 給予明確提示(非 admin 呼叫會被 rules 拒絕)
      const isPermissionDenied = (err as any)?.code === 'permission-denied' ||
        String((err as any)?.message || err).toLowerCase().includes('permission');
      setError(isPermissionDenied ? '沒有管理權限,僅管理員可檢視用戶列表' : '無法載入用戶列表');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleApproveUser = async (user: GoogleUser) => {
    setConfirmDialog({ open: true, user, action: 'approve' });
  };

  const handleRejectUser = async (user: GoogleUser) => {
    setConfirmDialog({ open: true, user, action: 'reject' });
  };

  const handleConfirmAction = async () => {
    const { user, action } = confirmDialog;
    if (!user || !action) return;

    try {
      setError(null);
      const isApprove = action === 'approve';
      
      await MultiAccountService.updateGoogleUserPermissions(
        user.uid,
        isApprove,
        user.allowedAccounts,
        user.role
      );

      // Update local state
      setUsers(prev =>
        prev.map(u =>
          u.uid === user.uid ? { ...u, isAllowed: isApprove } : u
        )
      );

    } catch (err) {
      console.error(`Error ${action}ing user:`, err);
      setError(`無法${action === 'approve' ? '批准' : '拒絕'}用戶`);
    } finally {
      setConfirmDialog({ open: false, user: null, action: null });
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ open: false, user: null, action: null });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const getStatusChip = (user: GoogleUser) => {
    if (user.role === 'admin') {
      return <Chip label="管理員" color="error" size="small" icon={<AdminIcon />} />;
    }
    if (user.isAllowed) {
      return <Chip label="已批准" color="success" size="small" icon={<ApproveIcon />} />;
    }
    return <Chip label="等待審核" color="warning" size="small" icon={<PersonIcon />} />;
  };

  const pendingUsers = users.filter(user => !user.isAllowed && user.role !== 'admin');
  const approvedUsers = users.filter(user => user.isAllowed);
  const adminUsers = users.filter(user => user.role === 'admin');
  // CLEAN-02:一般使用者卡片扣除 admin 重複計數,三張卡片加總 = 總用戶數
  const regularApprovedUsers = approvedUsers.filter(user => user.role !== 'admin');

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>載入用戶資料中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="用戶管理"
        icon={PeopleOutline}
        subtitle={`管理用戶權限和帳戶訪問權限 • ${users.length}個註冊用戶`}
        actions={
          <Tooltip title="重新載入用戶列表">
            <IconButton onClick={loadUsers} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="warning.main">
              等待審核
            </Typography>
            <Typography variant="h4">
              {pendingUsers.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" color="success.main">
              已批准用戶
            </Typography>
            <Typography variant="h4">
              {regularApprovedUsers.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" color="error.main">
              管理員
            </Typography>
            <Typography variant="h4">
              {adminUsers.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            所有用戶 ({users.length})
          </Typography>
          
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>用戶</TableCell>
                  <TableCell>電子郵件</TableCell>
                  <TableCell>角色</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>註冊時間</TableCell>
                  <TableCell>最後登入</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.uid}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }}>
                          {user.displayName?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">
                          {user.displayName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role === 'admin' ? '管理員' : '用戶'} 
                        color={user.role === 'admin' ? 'error' : 'default'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(user)}</TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : '從未'}
                    </TableCell>
                    <TableCell>
                      {user.role !== 'admin' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {!user.isAllowed ? (
                            <>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleApproveUser(user)}
                                startIcon={<ApproveIcon />}
                              >
                                批准
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleRejectUser(user)}
                                startIcon={<RejectIcon />}
                              >
                                拒絕
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              onClick={() => handleRejectUser(user)}
                              startIcon={<RejectIcon />}
                            >
                              撤銷權限
                            </Button>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        目前沒有用戶
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseDialog}>
        <DialogTitle>
          {confirmDialog.action === 'approve' ? '批准用戶' : '拒絕/撤銷用戶權限'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'approve' ? (
              <>
                確定要批准用戶 <strong>{confirmDialog.user?.email}</strong> 的訪問權限嗎？
                <br />
                批准後，該用戶將能夠登入並使用系統。
              </>
            ) : (
              <>
                確定要{confirmDialog.user?.isAllowed ? '撤銷' : '拒絕'}用戶 <strong>{confirmDialog.user?.email}</strong> 的訪問權限嗎？
                <br />
                {confirmDialog.user?.isAllowed ? 
                  '撤銷後，該用戶將無法再訪問系統。' : 
                  '拒絕後，該用戶將無法登入系統。'
                }
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button 
            onClick={handleConfirmAction} 
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            確認{confirmDialog.action === 'approve' ? '批准' : confirmDialog.user?.isAllowed ? '撤銷' : '拒絕'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
